import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import {
  BedDouble,
  Bath,
  Calendar,
  MapPin,
  Home,
  Check,
  Shield,
  Clock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
} from "lucide-react";
import unitModel from "../../lib/firebase/unitModel";
import { getUnitDisplayAddress } from "../../lib/unitAddressUtils";
import InquiryModal from "./InquiryModal";
import { APP_NAME } from "../../helpers/constants";
import { trackEvent } from "../../helpers/mixpanel";

/**
 * VacancyLandingPage - Premium public listing page
 *
 * This is the "velvet rope" lead magnet. Must look better than Zillow.
 * No phone numbers shown - all traffic goes through the inquiry form.
 *
 * Uses existing units collection for data.
 */
const VacancyLandingPage = () => {
  const { listingId } = useParams(); // Actually a unitId in units collection
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

  // Fetch unit data from existing units collection
  const {
    data: unit,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["unit", listingId],
    queryFn: async () => {
      const data = await unitModel.getOneById(listingId);
      return data;
    },
    enabled: !!listingId,
    retry: 1,
  });

  // Map unit data to listing format for compatibility
  const listing = useMemo(
    () =>
      unit
        ? {
            id: unit.id,
            address: getUnitDisplayAddress(unit),
            rentAmount: unit.currentRent || unit.baseRent,
            deposit: unit.securityDeposit,
            photos: unit.photos || [],
            description:
              unit.description ||
              `${unit.bedrooms} bed, ${
                unit.bathrooms
              } bath property at ${getUnitDisplayAddress(unit)}`,
            amenities: unit.amenities || [],
            bedrooms: unit.bedrooms,
            bathrooms: unit.bathrooms,
            sqft: unit.sqft,
            availableDate: unit.availableDate,
            leaseTerm: unit.leaseTerm,
            rules: {
              petsAllowed: unit.petsAllowed,
              petDeposit: unit.petDeposit,
              minCreditScore: unit.minCreditScore,
              minIncomeMultiplier: unit.minIncomeMultiplier || 3,
            },
            landlordId: unit.ownerId,
            landlordVerified: true,
            responseTime: "Usually responds within 24 hours",
          }
        : null,
    [unit]
  );

  // Track page view (fire and forget)
  useEffect(() => {
    if (listing) {
      trackEvent("vacancy_page_viewed", {
        unit_id: listingId,
        address: listing.address,
        rent_amount: listing.rentAmount,
      });
    }
  }, [listing, listingId]);

  // Image carousel controls
  const nextImage = () => {
    if (listing?.photos?.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === listing.photos.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (listing?.photos?.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? listing.photos.length - 1 : prev - 1
      );
    }
  };

  const handleRequestTour = () => {
    trackEvent("request_tour_clicked", {
      listing_id: listingId,
      address: listing?.address,
    });
    setIsInquiryModalOpen(true);
  };

  // Format rent amount
  const formatRent = (amount) => {
    if (!amount) return "Contact for pricing";
    return `$${amount.toLocaleString()}`;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "Available Now";
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Calculate if prospect would qualify
  const getRequirements = () => {
    const requirements = [];

    if (listing?.minCreditScore) {
      requirements.push(`Credit Score ${listing.minCreditScore}+`);
    }

    if (listing?.minIncomeMultiplier) {
      const requiredIncome = listing.rentAmount * listing.minIncomeMultiplier;
      requirements.push(
        `Income ${
          listing.minIncomeMultiplier
        }x Rent ($${requiredIncome.toLocaleString()}/mo)`
      );
    }

    if (listing?.petsAllowed === false) {
      requirements.push("No Pets");
    } else if (listing?.petsAllowed && listing?.petDeposit) {
      requirements.push(`Pets OK (${formatRent(listing.petDeposit)} deposit)`);
    }

    return requirements;
  };

  // Loading state
  if (isLoading) {
    return (
      <LoadingContainer>
        <Loader2 className="animate-spin" size={48} />
        <LoadingText>Loading property details...</LoadingText>
      </LoadingContainer>
    );
  }

  // Error/Not Found state
  if (isError || !listing) {
    return (
      <>
        <Helmet>
          <title>Listing Not Available | {APP_NAME}</title>
        </Helmet>
        <NotFoundContainer>
          <NotFoundIcon>
            <Home size={64} />
          </NotFoundIcon>
          <NotFoundTitle>This listing is no longer available</NotFoundTitle>
          <NotFoundText>
            The property you're looking for may have been rented or removed.
          </NotFoundText>
          <BackButton onClick={() => navigate("/")}>
            Return to Homepage
          </BackButton>
        </NotFoundContainer>
      </>
    );
  }

  // Get images or use placeholders
  const images =
    listing.photos && listing.photos.length > 0
      ? listing.photos
      : [
          "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1600&h=900&q=85&fit=crop",
          "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&h=900&q=85&fit=crop",
          "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&h=900&q=85&fit=crop",
        ];

  const requirements = getRequirements();

  return (
    <>
      <Helmet>
        <title>
          {listing.title || listing.address} - ${listing.rentAmount}/mo |{" "}
          {APP_NAME}
        </title>
        <meta
          name="description"
          content={
            listing.description ||
            `${listing.bedrooms} bed, ${listing.bathrooms} bath rental at ${
              listing.address
            }. ${formatRent(listing.rentAmount)}/mo.`
          }
        />
        <meta
          property="og:title"
          content={`${listing.address} - ${formatRent(listing.rentAmount)}/mo`}
        />
        <meta
          property="og:description"
          content={
            listing.description || "Beautiful rental property available now"
          }
        />
        <meta property="og:image" content={images[0]} />
        <meta property="og:type" content="website" />
      </Helmet>

      <PageContainer>
        {/* Hero Image Carousel */}
        <HeroCarousel>
          <HeroImage
            src={images[currentImageIndex]}
            alt={`${listing.address} - Photo ${currentImageIndex + 1}`}
          />
          <HeroOverlay />

          {images.length > 1 && (
            <>
              <CarouselButton direction="left" onClick={prevImage}>
                <ChevronLeft size={32} />
              </CarouselButton>
              <CarouselButton direction="right" onClick={nextImage}>
                <ChevronRight size={32} />
              </CarouselButton>
              <ImageCounter>
                {currentImageIndex + 1} / {images.length}
              </ImageCounter>
            </>
          )}
        </HeroCarousel>

        {/* Main Content */}
        <ContentContainer>
          <MainContent>
            {/* Property Header with Verified Badge */}
            <PropertyHeader>
              <HeaderTop>
                <AddressSection>
                  <Address>{listing.title || listing.address}</Address>
                  {listing.landlordVerified && (
                    <VerifiedBadge>
                      <Shield size={16} />
                      Verified Landlord
                    </VerifiedBadge>
                  )}
                </AddressSection>
                <PriceSection>
                  <Price>{formatRent(listing.rentAmount)}</Price>
                  <PriceLabel>/month</PriceLabel>
                </PriceSection>
              </HeaderTop>

              <Location>
                <MapPin size={18} />
                {listing.address}
                {listing.city &&
                  listing.state &&
                  `, ${listing.city}, ${listing.state}`}
              </Location>
            </PropertyHeader>

            {/* Quick Stats */}
            <StatsGrid>
              <StatItem>
                <BedDouble size={24} />
                <StatLabel>
                  {listing.bedrooms} Bed{listing.bedrooms !== 1 ? "s" : ""}
                </StatLabel>
              </StatItem>
              <StatItem>
                <Bath size={24} />
                <StatLabel>
                  {listing.bathrooms} Bath{listing.bathrooms !== 1 ? "s" : ""}
                </StatLabel>
              </StatItem>
              {listing.sqft && (
                <StatItem>
                  <Home size={24} />
                  <StatLabel>{listing.sqft.toLocaleString()} sqft</StatLabel>
                </StatItem>
              )}
              <StatItem>
                <Calendar size={24} />
                <StatLabel>{formatDate(listing.availableDate)}</StatLabel>
              </StatItem>
            </StatsGrid>

            {/* Requirements (Velvet Rope) */}
            {requirements.length > 0 && (
              <RequirementsSection>
                <SectionTitle>Requirements</SectionTitle>
                <RequirementsList>
                  {requirements.map((req, index) => (
                    <RequirementItem key={index}>
                      <Check size={20} />
                      {req}
                    </RequirementItem>
                  ))}
                </RequirementsList>
                <RequirementsNote>
                  <AlertCircle size={16} />
                  Meeting these requirements improves your chances of approval
                </RequirementsNote>
              </RequirementsSection>
            )}

            {/* Description */}
            <DescriptionSection>
              <SectionTitle>About This Property</SectionTitle>
              <Description>
                {listing.description ||
                  "Beautiful rental property in a great location."}
              </Description>
            </DescriptionSection>

            {/* Amenities */}
            {listing.amenities && listing.amenities.length > 0 && (
              <AmenitiesSection>
                <SectionTitle>Amenities</SectionTitle>
                <AmenitiesList>
                  {listing.amenities.map((amenity, index) => (
                    <AmenityItem key={index}>
                      <Check size={18} />
                      {amenity}
                    </AmenityItem>
                  ))}
                </AmenitiesList>
              </AmenitiesSection>
            )}

            {/* Landlord Response Time */}
            {listing.responseTime && (
              <ResponseTimeSection>
                <Clock size={20} />
                <ResponseTimeText>{listing.responseTime}</ResponseTimeText>
              </ResponseTimeSection>
            )}
          </MainContent>

          {/* Sticky CTA Sidebar (Desktop) */}
          <CTASidebar>
            <CTACard>
              <CTATitle>Interested in this property?</CTATitle>
              <CTAPrice>{formatRent(listing.rentAmount)}/mo</CTAPrice>

              <CTAButton onClick={handleRequestTour}>Request a Tour</CTAButton>

              <CTANote>
                No phone calls needed. We'll connect you directly with the
                landlord.
              </CTANote>

              {listing.securityDeposit && (
                <DepositInfo>
                  Security Deposit: {formatRent(listing.securityDeposit)}
                </DepositInfo>
              )}

              <TrustBadges>
                {listing.landlordVerified && (
                  <TrustBadge>
                    <Shield size={16} />
                    Verified Owner
                  </TrustBadge>
                )}
                <TrustBadge>
                  <Clock size={16} />
                  Fast Response
                </TrustBadge>
              </TrustBadges>
            </CTACard>
          </CTASidebar>
        </ContentContainer>

        {/* Mobile Sticky CTA */}
        <MobileCTA>
          <MobileCTAButton onClick={handleRequestTour}>
            Request a Tour - {formatRent(listing.rentAmount)}/mo
          </MobileCTAButton>
        </MobileCTA>

        {/* Inquiry Modal */}
        <InquiryModal
          listing={listing}
          isOpen={isInquiryModalOpen}
          onClose={() => setIsInquiryModalOpen(false)}
        />
      </PageContainer>
    </>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #f9fafb;
  padding-bottom: 80px; // Space for mobile CTA

  @media (min-width: 1024px) {
    padding-bottom: 0;
  }
`;

const HeroCarousel = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 350px;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.3) 100%
  );
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  ${(props) => props.direction}: 1rem;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;

  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  z-index: 10;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: -60px auto 0;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  position: relative;
  z-index: 5;
  text-align: left;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: -40px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: left;
`;

const PropertyHeader = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  text-align: left !important;

  @media (min-width: 641px) {
    text-align: left !important;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  text-align: left;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const AddressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left !important;
  align-items: flex-start;

  @media (min-width: 641px) {
    text-align: left !important;
    align-items: flex-start !important;
  }
`;

const Address = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.2;
  text-align: left !important;

  @media (min-width: 641px) {
    text-align: left !important;
  }

  @media (max-width: 640px) {
    font-size: 1.5rem;
  }
`;

const VerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #10b981;
  color: white;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  width: fit-content;

  svg {
    color: white;
  }
`;

const PriceSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const Price = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #059669;

  @media (max-width: 640px) {
    font-size: 2rem;
  }
`;

const PriceLabel = styled.span`
  font-size: 1.125rem;
  color: #6b7280;
  font-weight: 500;
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 1rem;
  text-align: left !important;
  justify-content: flex-start;

  @media (min-width: 641px) {
    text-align: left !important;
    justify-content: flex-start !important;
  }

  svg {
    color: #9ca3af;
  }
`;

const StatsGrid = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1.5rem;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;

  svg {
    color: #3b82f6;
  }
`;

const StatLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
`;

const RequirementsSection = styled.div`
  background: #eff6ff;
  border: 2px solid #3b82f6;
  padding: 1.5rem;
  border-radius: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  text-align: left;
  margin: 0 0 1rem 0;
`;

const RequirementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1e40af;
  font-weight: 500;

  svg {
    flex-shrink: 0;
    color: #3b82f6;
  }
`;

const RequirementsNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #6b7280;

  svg {
    flex-shrink: 0;
    color: #3b82f6;
  }
`;

const DescriptionSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  text-align: left !important;

  @media (min-width: 641px) {
    text-align: left !important;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #4b5563;
  margin: 0;
  white-space: pre-wrap;
  text-align: left !important;

  @media (min-width: 641px) {
    text-align: left !important;
  }
`;

const AmenitiesSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
`;

const AmenitiesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
`;

const AmenityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #374151;

  svg {
    color: #10b981;
    flex-shrink: 0;
  }
`;

const ResponseTimeSection = styled.div`
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  svg {
    color: #3b82f6;
  }
`;

const ResponseTimeText = styled.span`
  color: #374151;
  font-weight: 500;
`;

const CTASidebar = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const CTACard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CTATitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const CTAPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #059669;
`;

const CTAButton = styled.button`
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  background: #3b82f6;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);

  &:hover {
    background: #2563eb;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CTANote = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

const DepositInfo = styled.div`
  padding: 0.75rem;
  background: #f3f4f6;
  border-radius: 8px;
  text-align: center;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
`;

const TrustBadges = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;

  svg {
    color: #10b981;
  }
`;

const MobileCTA = styled.div`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 1rem;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileCTAButton = styled.button`
  width: 100%;
  padding: 1rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  background: #3b82f6;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;

  &:active {
    background: #2563eb;
  }
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const LoadingText = styled.p`
  font-size: 1.125rem;
  color: #6b7280;
`;

const NotFoundContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
`;

const NotFoundIcon = styled.div`
  color: #d1d5db;
  margin-bottom: 1.5rem;
`;

const NotFoundTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const NotFoundText = styled.p`
  font-size: 1.125rem;
  color: #6b7280;
  margin: 0 0 2rem 0;
  max-width: 500px;
`;

const BackButton = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: #3b82f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2563eb;
  }
`;

export default VacancyLandingPage;
